import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { followUser, unfollowUser } from '../discovery.api';
import { ArticleDetails } from '../../components/Settings/atom/initialState';
import { DataLayer } from '../../utils/data-layer';

export const useFollowUserMutation = (articleId) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      isFollowing,
      writerUsername
    }: {
      userId: string;
      isFollowing: boolean;
      writerUsername?: string;
    }) => {
      if (isFollowing) {
        return unfollowUser(userId);
      }

      // Track writer follow event when following (not unfollowing)
      if (writerUsername) {
        DataLayer.trackWriterFollow(writerUsername);
      }

      return followUser(userId);
    },
    onMutate: async ({ userId, isFollowing, writerUsername }) => {
      // Cancel any outgoing refetches to avoid optimistic update being overwritten
      await queryClient.cancelQueries({ queryKey: ['article'] });

      // Snapshot the previous article value
      const previousArticle = queryClient.getQueryData<ArticleDetails>(['article', articleId]);

      // Optimistically update the article
      if (previousArticle) {
        queryClient.setQueryData<ArticleDetails>(['article', articleId], {
          ...previousArticle
          // creator: {
          //   ...previousArticle.creator,
          //   isFollowing: !isFollowing
          // }
        });
      }

      // Return context with the snapshotted value
      return { previousArticle };
    },
    onError: (error, { isFollowing, writerUsername }, context) => {
      // Revert to the previous article on error
      if (context?.previousArticle) {
        queryClient.setQueryData(['article', articleId], context.previousArticle);
      }

      toast({
        title: "Couldn't update follow status",
        description: 'Please try again',
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
      console.error(error);
    },
    onSettled: () => {
      // Invalidate the article query to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
    }
  });
};
